import React, { useState, useEffect } from 'react'
import { Form, Button, Spinner, Row, Col } from 'react-bootstrap'
import styles from '../CustomDomainModal.module.css'
import { getCountries } from '../../../services/countries'
import { useFormikContext } from 'formik'

const RegistrantContactInfo = ({
  userProfile,
  onPrevious,
  onBuy,
  isLoading,
  isProcessing,
  formatTime,
  processingCountdown,
  showRetryMessage
}) => {
  const [countriesList, setCountriesList] = useState([])
  const formik = useFormikContext()
  const { values, handleChange, handleBlur, touched, errors } = formik

  useEffect(() => {
    if (userProfile) {
      if (!values.registrantContact.RegistrantFirstName) {
        const first =
          userProfile.firstName || userProfile.firstname || (userProfile.name ? userProfile.name.split(' ')[0] : '')
        if (first) formik.setFieldValue('registrantContact.RegistrantFirstName', first)
      }
      if (!values.registrantContact.RegistrantLastName) {
        const last =
          userProfile.lastName ||
          userProfile.lastname ||
          (userProfile.name ? userProfile.name.split(' ').slice(1).join(' ') : '')
        if (last) formik.setFieldValue('registrantContact.RegistrantLastName', last)
      }
      if (!values.registrantContact.RegistrantEmailAddress && userProfile.email) {
        formik.setFieldValue('registrantContact.RegistrantEmailAddress', userProfile.email)
      }
    }
  }, [])

  useEffect(() => {
    let isMounted = true
    getCountries()
      .then((data) => {
        if (!isMounted) return
        const sorted = data
          .map((c) => {
            const root = c.idd?.root || ''
            const suffix = c.idd?.suffixes && c.idd.suffixes.length > 0 ? c.idd.suffixes[0] : ''
            return {
              code: c.cca2,
              name: c.name.common,
              phoneCode: `${root}${suffix}`
            }
          })
          .sort((a, b) => a.name.localeCompare(b.name))
        setCountriesList(sorted)
      })
      .catch((err) => {
        if (!isMounted) return
        console.error('Failed to fetch countries', err)
      })
    return () => {
      isMounted = false
    }
  }, [])

  const isTouched = (field) => touched.registrantContact?.[field]
  const errorMsg = (field) => errors.registrantContact?.[field]

  return (
    <>
      <div className={styles.subHeading} style={{ textAlign: 'left', minHeight: '300px' }}>
        <div className={styles.title} style={{ textAlign: 'center' }}>
          Who will own this domain?
        </div>
        <div className={styles.subTitle} style={{ textAlign: 'center' }}>
          We require you to provide accurate contact information for the domain owner.
        </div>
        <div className="mt-4">
          <Row>
            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>First Name</Form.Label>
                <Form.Control
                  type="text"
                  name="registrantContact.RegistrantFirstName"
                  value={values.registrantContact.RegistrantFirstName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={isTouched('RegistrantFirstName') && !!errorMsg('RegistrantFirstName')}
                />
                {isTouched('RegistrantFirstName') && errorMsg('RegistrantFirstName') && (
                  <Form.Control.Feedback type="invalid">{errorMsg('RegistrantFirstName')}</Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group className="mb-3">
                <Form.Label>Last Name</Form.Label>
                <Form.Control
                  type="text"
                  name="registrantContact.RegistrantLastName"
                  value={values.registrantContact.RegistrantLastName}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={isTouched('RegistrantLastName') && !!errorMsg('RegistrantLastName')}
                />
                {isTouched('RegistrantLastName') && errorMsg('RegistrantLastName') && (
                  <Form.Control.Feedback type="invalid">{errorMsg('RegistrantLastName')}</Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>

          <Form.Group className="mb-3">
            <Form.Label>Email Address</Form.Label>
            <Form.Control
              type="email"
              name="registrantContact.RegistrantEmailAddress"
              value={values.registrantContact.RegistrantEmailAddress}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={isTouched('RegistrantEmailAddress') && !!errorMsg('RegistrantEmailAddress')}
            />
            {isTouched('RegistrantEmailAddress') && errorMsg('RegistrantEmailAddress') && (
              <Form.Control.Feedback type="invalid">{errorMsg('RegistrantEmailAddress')}</Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Country</Form.Label>
            <Form.Control
              as="select"
              name="registrantContact.RegistrantCountry"
              value={values.registrantContact.RegistrantCountry}
              onChange={(e) => {
                const newCountryName = e.target.value
                const oldCountryName = values.registrantContact.RegistrantCountry
                handleChange(e)

                const selectedCountry = countriesList.find((c) => c.name === newCountryName)
                if (selectedCountry && selectedCountry.phoneCode) {
                  const currentPhone = values.registrantContact.RegistrantPhone || ''
                  const newPrefix = selectedCountry.phoneCode + ' '

                  if (!currentPhone || currentPhone.trim() === '' || currentPhone === '+') {
                    formik.setFieldValue('registrantContact.RegistrantPhone', newPrefix)
                  } else {
                    const oldCountry = countriesList.find((c) => c.name === oldCountryName)
                    if (oldCountry && oldCountry.phoneCode) {
                      const oldPrefix = oldCountry.phoneCode + ' '
                      if (currentPhone.startsWith(oldPrefix)) {
                        formik.setFieldValue(
                          'registrantContact.RegistrantPhone',
                          newPrefix + currentPhone.substring(oldPrefix.length)
                        )
                      }
                    }
                  }
                }
              }}
              onBlur={handleBlur}
              isInvalid={isTouched('RegistrantCountry') && !!errorMsg('RegistrantCountry')}
              disabled={countriesList.length === 0}
            >
              <option value="">{countriesList.length === 0 ? 'Loading...' : 'Select a country...'}</option>
              {countriesList.map((country) => (
                <option key={country.code} value={country.name}>
                  {country.name} ({country.code})
                </option>
              ))}
            </Form.Control>
            {isTouched('RegistrantCountry') && errorMsg('RegistrantCountry') && (
              <Form.Control.Feedback type="invalid">{errorMsg('RegistrantCountry')}</Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Phone Number</Form.Label>
            <Form.Control
              type="text"
              name="registrantContact.RegistrantPhone"
              value={values.registrantContact.RegistrantPhone}
              onChange={(e) => {
                let val = e.target.value
                const selectedCountry = countriesList.find((c) => c.name === values.registrantContact.RegistrantCountry)
                if (selectedCountry && selectedCountry.phoneCode) {
                  const rawPrefix = selectedCountry.phoneCode
                  const prefix = rawPrefix.startsWith('+') ? rawPrefix : `+${rawPrefix}`

                  if (val.startsWith(prefix)) {
                    const rest = val.substring(prefix.length)
                    if (rest.length > 0 && rest[0] !== ' ') {
                      val = prefix + ' ' + rest.trimStart()
                    } else if (val === prefix) {
                      val = prefix + ' '
                    }
                  }
                }
                e.target.value = val
                handleChange(e)
              }}
              onBlur={handleBlur}
              isInvalid={isTouched('RegistrantPhone') && !!errorMsg('RegistrantPhone')}
              placeholder="+1 ..."
            />
            {isTouched('RegistrantPhone') && errorMsg('RegistrantPhone') && (
              <Form.Control.Feedback type="invalid">{errorMsg('RegistrantPhone')}</Form.Control.Feedback>
            )}
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Address</Form.Label>
            <Form.Control
              type="text"
              name="registrantContact.RegistrantAddress1"
              value={values.registrantContact.RegistrantAddress1}
              onChange={handleChange}
              onBlur={handleBlur}
              isInvalid={isTouched('RegistrantAddress1') && !!errorMsg('RegistrantAddress1')}
            />
            {isTouched('RegistrantAddress1') && errorMsg('RegistrantAddress1') && (
              <Form.Control.Feedback type="invalid">{errorMsg('RegistrantAddress1')}</Form.Control.Feedback>
            )}
          </Form.Group>

          <Row>
            <Col md={4}>
              <Form.Group className="mb-4">
                <Form.Label>City</Form.Label>
                <Form.Control
                  type="text"
                  name="registrantContact.RegistrantCity"
                  value={values.registrantContact.RegistrantCity}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={isTouched('RegistrantCity') && !!errorMsg('RegistrantCity')}
                />
                {isTouched('RegistrantCity') && errorMsg('RegistrantCity') && (
                  <Form.Control.Feedback type="invalid">{errorMsg('RegistrantCity')}</Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-4">
                <Form.Label>State / Province</Form.Label>
                <Form.Control
                  type="text"
                  name="registrantContact.RegistrantStateProvince"
                  value={values.registrantContact.RegistrantStateProvince}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={isTouched('RegistrantStateProvince') && !!errorMsg('RegistrantStateProvince')}
                />
                {isTouched('RegistrantStateProvince') && errorMsg('RegistrantStateProvince') && (
                  <Form.Control.Feedback type="invalid">{errorMsg('RegistrantStateProvince')}</Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>

            <Col md={4}>
              <Form.Group className="mb-4">
                <Form.Label>Postal Code</Form.Label>
                <Form.Control
                  type="text"
                  name="registrantContact.RegistrantPostalCode"
                  value={values.registrantContact.RegistrantPostalCode}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  isInvalid={isTouched('RegistrantPostalCode') && !!errorMsg('RegistrantPostalCode')}
                />
                {isTouched('RegistrantPostalCode') && errorMsg('RegistrantPostalCode') && (
                  <Form.Control.Feedback type="invalid">{errorMsg('RegistrantPostalCode')}</Form.Control.Feedback>
                )}
              </Form.Group>
            </Col>
          </Row>
        </div>
      </div>
      <div className={styles.btnContainer}>
        <Button
          variant="outline-primary"
          onClick={onPrevious}
          size="small"
          showLeftArrow={true}
          showArrow={false}
          className={styles.backArrow}
        >
          Back
        </Button>
        {isProcessing ? (
          <Button size="small" className={styles.forwardButton} disabled={true}>
            Processing... {formatTime(processingCountdown)}
          </Button>
        ) : (
          <Button
            onClick={async () => {
              const fields = [
                'RegistrantFirstName',
                'RegistrantLastName',
                'RegistrantEmailAddress',
                'RegistrantPhone',
                'RegistrantAddress1',
                'RegistrantCity',
                'RegistrantStateProvince',
                'RegistrantPostalCode',
                'RegistrantCountry'
              ]
              fields.forEach((f) => formik.setFieldTouched(`registrantContact.${f}`, true, true))

              const errors = await formik.validateForm()
              if (!errors.registrantContact) {
                onBuy()
              }
            }}
            size="small"
            className={styles.forwardButton}
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner animation="border" variant="light" size="sm" />
            ) : showRetryMessage ? (
              'Retry Domain Purchase'
            ) : (
              'Buy domain'
            )}
          </Button>
        )}
      </div>
    </>
  )
}

export default RegistrantContactInfo
